<template>
  <footer
    class="px-24 py-8 bg-moon-grey border-t border-proton-grey border-solid"
  >
    <nav class="flex justify-between">
      <div
        class="flex items-center"
        :class="[$store.state.openCodeSandbox ? 'w-1/3' : 'w-1/6']"
      >
        <router-link
          @click.native="$store.commit('resetState')"
          v-show="atLastStep"
          tag="button"
          :to="{ name: '1' }"
          class="flex items-center py-16 rounded-sm focus:outline-none text-nebula-blue font-semibold leading-tight"
        >
          <arrow-left class="h-12 mr-8 fill-current" />Start over
        </router-link>
        <button
          @click="$store.commit('closeCodeSandbox')"
          v-show="$store.state.openCodeSandbox"
          class="flex items-center py-16 rounded-sm text-nebula-blue font-semibold leading-tight"
        >
          <arrow-left class="h-12 mr-8 fill-current" />Back to Step 4: Build UI
        </button>
      </div>
      <div
        class="flex items-center justify-center"
        :class="[$store.state.openCodeSandbox ? 'w-1/3' : 'w-2/3']"
      >
        <ul
          v-show="!$store.state.openCodeSandbox"
          class="flex items-center list-reset"
        >
          <template v-for="(step, index) in steps">
            <router-link
              tag="li"
              :data-step="getStepNumber(index)"
              :event="step.visited ? 'click' : ''"
              :to="{ name: getStepNumber(index) }"
              :key="step.id"
              class="flex group focus:outline-none"
              :class="[
                isActive(getStepNumber(index))
                  ? 'text-nebula-blue cursor-default'
                  : step.completed
                  ? 'text-mercury-1 cursor-pointer'
                  : step.visited
                  ? 'text-grey-chateau cursor-pointer'
                  : 'text-grey-chateau cursor-default'
              ]"
            >
              <span
                class="flex justify-center items-center w-24 h-24 text-center rounded-full transition-fast transition-in-out"
                :class="[
                  isActive(getStepNumber(index))
                    ? 'bg-nebula-blue text-white'
                    : step.completed
                    ? 'bg-mercury-1 text-white'
                    : 'bg-proton-grey text-telluric-blue',
                  {
                    'group-hover:bg-nova-grey group-hover:text-cosmos-black': isHoverable(
                      index
                    )
                  }
                ]"
              >
                <template class="text-white" v-if="step.completed">
                  <check class="w-8 fill-current" />
                </template>
                <template v-else>{{ getStepNumber(index) }}</template>
              </span>
              <span
                class="hidden lg:block ml-8 whitespace-no-wrap transition-fast transition-in-out"
                :class="{ 'group-hover:text-nova-grey': isHoverable(index) }"
                >{{ step.label }}</span
              >
            </router-link>
            <li
              v-if="index !== steps.length - 1"
              :key="step.id"
              class="w-32 h-1 mx-8 bg-proton-grey"
            />
          </template>
        </ul>
        <p
          class="text-center text-grey-chateau text-sm"
          v-show="
            $store.state.openCodeSandbox &&
              $store.getters.getUiLibrary.id === 'angular'
          "
        >
          Angular demos may be slow in CodeSandbox
        </p>
      </div>
      <div
        class="flex items-center justify-end"
        :class="[$store.state.openCodeSandbox ? 'w-1/3' : 'w-1/6']"
      >
        <router-link
          @click.native="onClick"
          tag="button"
          :to="{ name: getNextStepNumber }"
          :event="getCurrentStep.completed ? 'click' : ''"
          v-show="!atLastStep"
          :class="[
            getCurrentStep.completed
              ? 'bg-nebula-blue hover:bg-neptune-1 focus:bg-neptune-1'
              : 'bg-proton-grey cursor-default'
          ]"
          class="flex items-center py-16 px-24 rounded-sm focus:outline-none text-white font-semibold leading-tight whitespace-no-wrap"
          :data-step="!atLastStep ? getNextStepNumber : ''"
        >
          <template v-if="!$store.state.openCodeSandbox">
            Next step
            <arrow-right class="h-12 ml-8 fill-current" />
          </template>
          <template v-else
            >Finish</template
          >
        </router-link>
      </div>
    </nav>
  </footer>
</template>

<script>
import ArrowRight from '@/assets/images/arrow-right.svg'
import ArrowLeft from '@/assets/images/arrow-left.svg'
import Check from '@/assets/images/check.svg'

import track from '@/scripts/Track'

export default {
  props: {
    currentStep: {
      type: Number,
      default: 1
    }
  },
  components: { ArrowRight, ArrowLeft, Check },
  data() {
    return {
      steps: this.$store.state.steps
    }
  },
  computed: {
    getCurrentStep() {
      return this.steps[this.currentStep - 1]
    },
    getPreviousStepNumber() {
      return this.currentStep - 1
    },
    getNextStepNumber() {
      return this.currentStep + 1
    },
    atLastStep() {
      return this.currentStep === 5
    }
  },
  methods: {
    getStepNumber(index) {
      return index + 1
    },
    isActive(stepNumber) {
      return stepNumber === this.currentStep
    },
    isHoverable(index) {
      return (
        this.steps[index].visited &&
        !this.steps[index].completed &&
        !this.isActive(this.getStepNumber(index))
      )
    },
    onClick() {
      if (this.$store.state.openCodeSandbox)
        this.$store.commit('closeCodeSandbox')
      track({
        message: 'Clicked Next Step',
        code: 'docs.onboarding.next.step',
        extraData: {
          step: this.getCurrentStep.label,
          dataset: this.$store.getters.getPickDatasetsDataset.label,
          pushDataLanguage: this.$store.getters.getPushDataDataset.label,
          setRelevanceLanguage: this.$store.getters.getSetRelevanceDataset
            .label,
          uiLibrary: this.$store.getters.getUiLibrary.label
        }
      })
    }
  }
}
</script>
